import { isAnalyticsPathExcluded, isDevEnv } from "./analyticsEnv";
import { pushToDataLayer } from "./gtm";
import { getSessionUtmPayloadWithNulls } from "./utmTracking";

const ENTRY_POINT_VALUES = {
  HOMEPAGE: "homepage",
  /** Arrived from another website (Google, LinkedIn, etc.). */
  EXTERNAL: "external",
  /** Navigated from another page on cricket8 (not homepage). */
  INTERNAL: "internal",
  PROMO: "promo",
  REFERRAL: "referral",
  GUEST_SPIN: "guest_spin",
};

export const ENTRY_POINT_GUEST_SPIN = ENTRY_POINT_VALUES.GUEST_SPIN;
export const ENTRY_POINT_CHALLENGE = "challenge";

const PAGE_NAME_MAP = {
  "/": "homepage",
  "/signup": "signup",
  "/login": "login",
  "/shop": "shop",
  "/my-games": "my_games",
  "/my-zone": "my_zone",
  "/account": "account",
  "/trader": "trader",
  "/squares": "squares",
  "/bullion": "bullion",
};

function getDeviceType() {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth || 1280;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/** ISO-8601 week string, e.g. `2026-W22`, for `user_cohort` on signup. */
export function getCurrentISOWeek(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function getPageName(pathname) {
  if (!pathname) return "unknown";
  if (pathname === "/matches" && typeof window !== "undefined") {
    const matchId = new URLSearchParams(window.location.search).get("id");
    if (matchId != null && String(matchId).trim() !== "") return "match_detail";
    return "match_centre";
  }
  if (PAGE_NAME_MAP[pathname]) return PAGE_NAME_MAP[pathname];
  return pathname.replace(/^\//, "").replace(/-/g, "_") || "homepage";
}

function getSessionId() {
  if (typeof window === "undefined") return undefined;
  try {
    return (
      sessionStorage.getItem("c8_session_id") ||
      localStorage.getItem("c8_session_id") ||
      undefined
    );
  } catch (_) {
    return undefined;
  }
}

/**
 * Authenticated lifecycle context injected by the backend on page load
 * (`window.__C8__`). Returns nulls when unavailable so the keys are still
 * sent on every event (per tracking spec: send key with `null`, do not omit).
 */
function getLifecycleContext() {
  if (typeof window === "undefined") return { user_id: null, days_since_registration: null, user_cohort: null };
  const c8 = window.__C8__ || {};
  return {
    user_id: c8.user_id != null && c8.user_id !== "" ? String(c8.user_id) : null,
    days_since_registration:
      c8.days_since_registration != null && !Number.isNaN(Number(c8.days_since_registration))
        ? Number(c8.days_since_registration)
        : null,
    user_cohort:
      c8.user_cohort != null && String(c8.user_cohort) !== "" ? String(c8.user_cohort) : null,
  };
}

export function inferEntryPoint(search = "", referrer = "") {
  if (typeof window === "undefined") return ENTRY_POINT_VALUES.HOMEPAGE;
  const params = new URLSearchParams(search);
  const game = (params.get("game") || "").trim().toLowerCase();
  const gamesession = (params.get("gamesession") || "").trim();
  if (game === "wheel-spin" && gamesession) {
    return ENTRY_POINT_VALUES.GUEST_SPIN;
  }
  const hasReferral =
    params.has("ref") || params.has("referral") || params.has("invite");
  if (hasReferral) return ENTRY_POINT_VALUES.REFERRAL;

  const promoSource =
    params.get("promo") ||
    params.get("offer") ||
    params.get("utm_campaign") ||
    "";
  if (promoSource && /(promo|offer|campaign|bonus|welcome)/i.test(promoSource)) {
    return ENTRY_POINT_VALUES.PROMO;
  }

  if (referrer) {
    try {
      const refUrl = new URL(referrer);
      if (refUrl.origin !== window.location.origin) {
        return ENTRY_POINT_VALUES.EXTERNAL;
      }
      if (refUrl.pathname === "/" || refUrl.pathname === "") {
        return ENTRY_POINT_VALUES.HOMEPAGE;
      }
      return ENTRY_POINT_VALUES.INTERNAL;
    } catch (_) {
      return ENTRY_POINT_VALUES.EXTERNAL;
    }
  }

  return ENTRY_POINT_VALUES.HOMEPAGE;
}

function stripNilFields(obj) {
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    if (out[k] === undefined || out[k] === null) delete out[k];
  });
  return out;
}

/**
 * GTM dataLayer last-value variables retain prior keys when a later push omits them.
 * Explicit `null` clears those keys so stale `game_type` / `entry_point` / etc. cannot leak.
 */
const DATA_LAYER_CLEAR_KEYS = [
  "game_type",
  "entry_point",
  "sport",
  "submission_type",
  "prediction_id",
  "market_name",
  "selection_value",
  "fixture_id",
  "competition_name",
  "cta_name",
  "cta_location",
  "source_page",
  "play_mode",
  "league_id",
  "is_league_play",
];

const C8_GAME_ENTRY_KEY = "c8_game_entry";

export const ENTRY_POINT_MATCH_CENTRE = "match_centre";
export const ENTRY_POINT_MATCH_DETAIL = "match_detail";
export const ENTRY_POINT_TRADER_LOBBY = "trader_lobby";
export const ENTRY_POINT_SQUARES_LOBBY = "squares_lobby";
export const ENTRY_POINT_BULLION_LOBBY = "bullion_lobby";
export const ENTRY_POINT_SIXES_LOBBY = "sixes_lobby";
export const ENTRY_POINT_PROFILE_INVITE = "profile_invite";
/** Deep link / refresh / unknown origin into a match (controlled fallback). */
export const ENTRY_POINT_DIRECT_LINK = "direct_link";
/** Opened a game from a Cricket8 League (same game_type as the original game). */
export const ENTRY_POINT_LEAGUE = "league";

const LOBBY_ENTRY_BY_GAME_TYPE = {
  trader: ENTRY_POINT_TRADER_LOBBY,
  squares: ENTRY_POINT_SQUARES_LOBBY,
  bullion: ENTRY_POINT_BULLION_LOBBY,
  sixes: ENTRY_POINT_SIXES_LOBBY,
};

/**
 * Map match-card / lobby `cta_location` to spec `entry_point`.
 */
export function entryPointFromCtaLocation(cta_location) {
  if (cta_location == null || cta_location === "") return undefined;
  if (cta_location === ENTRY_POINT_MATCH_DETAIL) return ENTRY_POINT_MATCH_DETAIL;
  if (
    cta_location === "homepage_match_centre" ||
    cta_location === "matches_page" ||
    cta_location === "match_card"
  ) {
    return ENTRY_POINT_MATCH_CENTRE;
  }
  if (cta_location === "trader_lobby" || cta_location === "match_card_header") {
    return ENTRY_POINT_TRADER_LOBBY;
  }
  if (cta_location === "squares_lobby") return ENTRY_POINT_SQUARES_LOBBY;
  if (cta_location === "bullion_lobby" || cta_location === "bullion_match_landing") {
    return ENTRY_POINT_BULLION_LOBBY;
  }
  if (cta_location === "sixes_lobby") return ENTRY_POINT_SIXES_LOBBY;
  if (cta_location === "games_home") return "games_home";
  if (cta_location === ENTRY_POINT_LEAGUE || cta_location === "leagues") return ENTRY_POINT_LEAGUE;
  return undefined;
}

/** Normalize display / legacy labels to controlled `game_type` values. */
export function normalizeGameType(raw) {
  if (raw == null || String(raw).trim() === "") return undefined;
  const s = String(raw).trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
  if (s === "wheel-spin" || s === "wheelspin" || s === "spinner" || s === "spin") {
    return "wheel-spin";
  }
  return s;
}

/** Remember acquisition path for the current game session (cleared on mismatch). */
export function persistGameEntryPoint({ entry_point, fixture_id, game_type }) {
  if (!entry_point || typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      C8_GAME_ENTRY_KEY,
      JSON.stringify({
        entry_point: String(entry_point),
        fixture_id: fixture_id != null ? String(fixture_id) : null,
        game_type: game_type != null ? String(game_type) : null,
      }),
    );
  } catch (_) {
    /* ignore */
  }
}

/** Mark the current game session as entered from a Cricket8 League. */
export function persistLeagueGameEntryPoint({ fixture_id, game_type } = {}) {
  persistGameEntryPoint({
    entry_point: ENTRY_POINT_LEAGUE,
    fixture_id,
    game_type,
  });
}

const PLAY_MODE_LEAGUE = "league";
const PLAY_MODE_ORIGINAL = "original";

/** Game events that should always carry play_mode / is_league_play for report splits. */
const PLAY_SURFACE_EVENTS = new Set([
  "game_lobby_view",
  "match_selected",
  "game_started",
  "game_interaction",
  "prediction_submitted",
  "prediction_cancelled",
  "game_entry_clicked",
  "how_to_play_view",
  "sixes_card_revealed",
  "sixes_multiplier_selected",
  "sixes_card_settled",
  "sixes_board_viewed",
]);

function leagueIdFromSearch(search) {
  try {
    const qs =
      search != null
        ? search
        : typeof window !== "undefined"
          ? window.location.search || ""
          : "";
    const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    const id = params.get("leagueId");
    return id != null && String(id).trim() !== "" ? String(id).trim() : undefined;
  } catch (_) {
    return undefined;
  }
}

/**
 * Report split: same event names / game_type, explicit league vs original.
 * `league_id` is null on original so sticky GTM vars cannot leak a prior league.
 */
function playSurfaceFields({ play_mode, league_id, search } = {}) {
  const explicitId =
    league_id != null && String(league_id).trim() !== "" ? String(league_id).trim() : undefined;
  const inferredId = leagueIdFromSearch(search);
  const isLeague =
    play_mode === PLAY_MODE_LEAGUE || play_mode === true || Boolean(explicitId || inferredId);
  return {
    play_mode: isLeague ? PLAY_MODE_LEAGUE : PLAY_MODE_ORIGINAL,
    is_league_play: isLeague,
    league_id: isLeague ? explicitId || inferredId || null : null,
  };
}

/** Extra GTM params so league plays roll into original `game_type` but stay filterable. */
function withLeaguePlayMode(params, extras = {}) {
  return { ...params, ...playSurfaceFields(extras) };
}

/** Clear persisted game entry when the user leaves that fixture/game context. */
export function clearGameEntryPoint() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(C8_GAME_ENTRY_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Last persisted `entry_point` when fixture/game still match (e.g. game_started after card click). */
export function resolveGameEntryPoint({ fixture_id, game_type } = {}) {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = sessionStorage.getItem(C8_GAME_ENTRY_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed?.entry_point) return undefined;
    if (
      fixture_id != null &&
      parsed.fixture_id != null &&
      String(parsed.fixture_id) !== String(fixture_id)
    ) {
      clearGameEntryPoint();
      return undefined;
    }
    if (
      game_type != null &&
      parsed.game_type != null &&
      String(parsed.game_type) !== String(game_type)
    ) {
      clearGameEntryPoint();
      return undefined;
    }
    return String(parsed.entry_point);
  } catch (_) {
    return undefined;
  }
}

/**
 * Best-effort entry when session storage is empty: URL param → same-origin referrer →
 * current path lobby → `direct_link` (gameplay events only).
 */
export function inferGameplayEntryPointFallback({ game_type, pathname, search } = {}) {
  if (typeof window === "undefined") return undefined;
  const path = pathname || window.location.pathname || "";
  const qs = search != null ? search : window.location.search || "";

  try {
    const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    const fromParam =
      params.get("entry_point") ||
      params.get("entry") ||
      params.get("from") ||
      "";
    if (fromParam.trim()) {
      const normalized = String(fromParam).trim().toLowerCase().replace(/\s+/g, "_");
      if (normalized) return normalized;
    }
    if (params.get("leagueId")?.trim()) return ENTRY_POINT_LEAGUE;
  } catch (_) {
    /* ignore */
  }

  try {
    if (document.referrer) {
      const refUrl = new URL(document.referrer);
      if (refUrl.origin === window.location.origin) {
        const refPath = refUrl.pathname || "";
        if (refPath === "/" || refPath === "") return ENTRY_POINT_VALUES.HOMEPAGE;
        if (refPath.startsWith("/leagues")) return ENTRY_POINT_LEAGUE;
        if (refPath === "/matches") return ENTRY_POINT_MATCH_CENTRE;
        if (refPath.startsWith("/trader")) return ENTRY_POINT_TRADER_LOBBY;
        if (refPath.startsWith("/squares")) return ENTRY_POINT_SQUARES_LOBBY;
        if (refPath.startsWith("/bullion")) return ENTRY_POINT_BULLION_LOBBY;
        if (refPath.startsWith("/sixes")) return ENTRY_POINT_SIXES_LOBBY;
        if (refPath.startsWith("/my-games") || refPath.startsWith("/my-zone")) {
          return "my_games";
        }
        if (refPath.startsWith("/profile")) return ENTRY_POINT_PROFILE_INVITE;
      }
    }
  } catch (_) {
    /* ignore */
  }

  // On a game route: lobby list vs in-match deep link / refresh.
  try {
    const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    if (params.get("leagueId")?.trim()) return ENTRY_POINT_LEAGUE;
    const hasMatchContext =
      Boolean(params.get("matchId") || params.get("raceId") || params.get("match")) ||
      Boolean(params.get("room") || params.get("gameId"));
    if (path.startsWith("/trader")) {
      return hasMatchContext ? ENTRY_POINT_DIRECT_LINK : ENTRY_POINT_TRADER_LOBBY;
    }
    if (path.startsWith("/squares")) {
      return hasMatchContext ? ENTRY_POINT_DIRECT_LINK : ENTRY_POINT_SQUARES_LOBBY;
    }
    if (path.startsWith("/bullion")) {
      return hasMatchContext ? ENTRY_POINT_DIRECT_LINK : ENTRY_POINT_BULLION_LOBBY;
    }
    if (path.startsWith("/sixes")) {
      return hasMatchContext ? ENTRY_POINT_DIRECT_LINK : ENTRY_POINT_SIXES_LOBBY;
    }
  } catch (_) {
    if (path.startsWith("/trader")) return ENTRY_POINT_TRADER_LOBBY;
    if (path.startsWith("/squares")) return ENTRY_POINT_SQUARES_LOBBY;
    if (path.startsWith("/bullion")) return ENTRY_POINT_BULLION_LOBBY;
    if (path.startsWith("/sixes")) return ENTRY_POINT_SIXES_LOBBY;
  }

  const lobbyDefault = game_type != null ? LOBBY_ENTRY_BY_GAME_TYPE[String(game_type)] : undefined;
  if (lobbyDefault) return lobbyDefault;

  return ENTRY_POINT_DIRECT_LINK;
}

/**
 * Resolve gameplay `entry_point`: explicit → session → URL/referrer/path → optional fallback.
 * Pass `fallback: true` on funnel events that should rarely be null (game_started, prediction, etc.).
 */
function withResolvedEntryPoint(explicit, context = {}, { fallback = false } = {}) {
  if (explicit != null && String(explicit) !== "") return String(explicit);
  const fromSession = resolveGameEntryPoint(context);
  if (fromSession) return fromSession;
  if (!fallback) return undefined;
  return inferGameplayEntryPointFallback({
    game_type: context.game_type,
    pathname: context.pathname,
  });
}

const C8_LAST_SPORT_KEY = "c8_sport";

/** Persist last active sport (spec value) so monetisation events can attribute by sport. */
export function persistLastSport(sport) {
  if (typeof window === "undefined") return;
  const s = sport != null && String(sport).trim() !== "" ? String(sport) : null;
  if (!s) return;
  try {
    sessionStorage.setItem(C8_LAST_SPORT_KEY, s);
  } catch (_) {
    /* ignore */
  }
}

/** Last active sport for monetisation attribution; null when no context yet. */
export function getLastSport() {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(C8_LAST_SPORT_KEY);
    return v != null && v !== "" ? v : null;
  } catch (_) {
    return null;
  }
}

/** Map provider currency codes to spec values (INR, COINS, RUBIES). */
export function normalizeShopCurrency(currency) {
  if (currency == null || currency === "") return "INR";
  const s = String(currency).toLowerCase();
  if (s === "inr") return "INR";
  if (s === "cr8_rby" || s === "rubies" || s === "ruby") return "RUBIES";
  if (s === "cr8_coin" || s === "coins" || s === "coin") return "COINS";
  return String(currency).toUpperCase();
}

function pushEvent(eventName, eventParams = {}, context = {}) {
  if (isDevEnv()) return;
  const pathname =
    context.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  if (isAnalyticsPathExcluded(pathname)) return;
  const sessionId = getSessionId();
  const lifecycle = getLifecycleContext();
  const payload = stripNilFields({
    event: eventName,
    page_name: getPageName(pathname),
    user_status: context.userStatus || "guest",
    platform: "web",
    device_type: getDeviceType(),
    ...stripNilFields(eventParams),
  });
  if (sessionId) payload.session_id = sessionId;

  // Lifecycle context: always present (null when unavailable) per spec.
  // Explicit event params take precedence over the global window.__C8__ values.
  if (!("days_since_registration" in payload)) {
    payload.days_since_registration = lifecycle.days_since_registration;
  }
  if (!("user_cohort" in payload)) {
    payload.user_cohort = lifecycle.user_cohort;
  }

  if (context.userId != null && context.userId !== "") {
    payload.user_id = String(context.userId);
  } else if (payload.user_id == null && lifecycle.user_id != null) {
    payload.user_id = lifecycle.user_id;
  } else if (payload.user_id == null) {
    payload.user_id = null;
  }

  if (PLAY_SURFACE_EVENTS.has(eventName) || payload.game_type) {
    const surface = playSurfaceFields({
      play_mode: payload.play_mode,
      league_id: payload.league_id,
      search: typeof window !== "undefined" ? window.location.search : undefined,
    });
    payload.play_mode = surface.play_mode;
    payload.is_league_play = surface.is_league_play;
    payload.league_id = surface.league_id;
  }

  // Reset sticky GTM vars when this event does not set them (prevents cross-game leakage).
  for (const key of DATA_LAYER_CLEAR_KEYS) {
    if (!(key in payload)) {
      payload[key] = null;
    }
  }

  pushToDataLayer({ dataLayer: payload }).catch(() => {
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  });
}

/**
 * Push `user_context_loaded` once authenticated lifecycle context is available
 * (`window.__C8__`). Should fire before other behavioural events on a page load.
 */
export function trackUserContextLoaded({ user_id, pathname, userStatus } = {}) {
  pushEvent(
    "user_context_loaded",
    {},
    {
      pathname,
      userStatus: userStatus || (user_id != null && user_id !== "" ? "logged_in" : "guest"),
      userId: user_id,
    }
  );
}

const C8_REFERRAL_KEY = "c8_referral";
const C8_PROFILE_REFERRAL_KEY = "c8_profile_referral";

/** Persist profile-link sender for signup attribution (`referral_source: friend_invite`). */
export function persistProfileReferralForSignup(sender_user_id) {
  if (sender_user_id == null || String(sender_user_id).trim() === "") return;
  try {
    sessionStorage.setItem(
      C8_PROFILE_REFERRAL_KEY,
      JSON.stringify({
        referral_code: String(sender_user_id).trim(),
        referral_source: "friend_invite",
      })
    );
  } catch (_) {
    /* ignore */
  }
}

/** Referral controlled values from the URL; null when no referral context is present. */
export function readReferralFromUrl(search) {
  let qs = search;
  if (qs == null) {
    qs = typeof window !== "undefined" ? window.location.search : "";
  }
  try {
    const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    const code =
      params.get("ref") ||
      params.get("referral") ||
      params.get("invite") ||
      params.get("invitedBy") ||
      null;
    if (!code) return { referral_code: null, referral_source: null };
    let source = "promo";
    if (params.has("invite") || params.has("invitedBy") || params.has("referral")) {
      source = "friend_invite";
    } else if (params.has("ref")) {
      source = "affiliate";
    }
    return { referral_code: String(code), referral_source: source };
  } catch (_) {
    return { referral_code: null, referral_source: null };
  }
}

/** Resolve referral from the URL, falling back to a value persisted at signup start. */
function resolveReferral() {
  const fromUrl = readReferralFromUrl();
  if (fromUrl.referral_code) {
    try {
      sessionStorage.setItem(C8_REFERRAL_KEY, JSON.stringify(fromUrl));
    } catch (_) {
      /* ignore */
    }
    return fromUrl;
  }
  try {
    const raw = sessionStorage.getItem(C8_REFERRAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.referral_code) return parsed;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    const rawProfile = sessionStorage.getItem(C8_PROFILE_REFERRAL_KEY);
    if (rawProfile) {
      const parsed = JSON.parse(rawProfile);
      if (parsed && parsed.referral_code) return parsed;
    }
  } catch (_) {
    /* ignore */
  }
  return { referral_code: null, referral_source: null };
}

/**
 * Signup `entry_point`: friend-invite / profile-link paths use `profile_invite`;
 * otherwise fall back to URL/referrer inference.
 */
export function resolveSignupEntryPoint(search = "", referrer = "", explicit) {
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit).trim();
  }
  const ref = resolveReferral();
  if (ref.referral_source === "friend_invite") {
    return ENTRY_POINT_PROFILE_INVITE;
  }
  return inferEntryPoint(search, referrer);
}

export function trackSignupStarted({
  signup_method,
  entry_point,
  referral_code,
  referral_source,
  pathname,
}) {
  const ref =
    referral_code !== undefined || referral_source !== undefined
      ? { referral_code: referral_code ?? null, referral_source: referral_source ?? null }
      : resolveReferral();
  // Friend-invite / profile-link always wins over URL/referrer inference (e.g. external).
  const resolvedEntry =
    ref.referral_source === "friend_invite"
      ? ENTRY_POINT_PROFILE_INVITE
      : entry_point != null && String(entry_point).trim() !== ""
        ? String(entry_point)
        : undefined;
  pushEvent(
    "signup_started",
    {
      ...getSessionUtmPayloadWithNulls(),
      ...stripNilFields({
        signup_method,
        entry_point: resolvedEntry,
        referral_code: ref.referral_code,
        referral_source: ref.referral_source,
      }),
    },
    { pathname, userStatus: "guest" }
  );
}

export function trackSignupCompleted({
  user_id,
  signup_method,
  entry_point,
  referral_code,
  referral_source,
  user_cohort,
  pathname,
}) {
  const ref =
    referral_code !== undefined || referral_source !== undefined
      ? { referral_code: referral_code ?? null, referral_source: referral_source ?? null }
      : resolveReferral();
  const resolvedEntry =
    ref.referral_source === "friend_invite"
      ? ENTRY_POINT_PROFILE_INVITE
      : entry_point != null && String(entry_point).trim() !== ""
        ? String(entry_point)
        : undefined;
  const uid = user_id != null && String(user_id).trim() !== "" ? String(user_id) : null;
  pushEvent(
    "signup_completed",
    {
      ...getSessionUtmPayloadWithNulls(),
      ...stripNilFields({
        user_id: uid,
        signup_method,
        entry_point: resolvedEntry,
        referral_code: ref.referral_code,
        referral_source: ref.referral_source,
        days_since_registration: 0,
        user_cohort: user_cohort ?? getCurrentISOWeek(),
        // Matches server Measurement Protocol `sign_up` for GA4 client/server dedupe.
        event_id: uid ? `sign_up:${uid}` : undefined,
      }),
    },
    { pathname, userStatus: "logged_in", userId: uid }
  );
}

export function trackLoginSuccess({
  user_id,
  login_method,
  pathname,
}) {
  pushEvent(
    "login_success",
    { user_id, login_method },
    { pathname, userStatus: "logged_in" }
  );
}

const C8_SHOP_PENDING_PURCHASE_KEY = "c8_shop_pending_purchase";

export function setPendingShopPurchase(payload) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(C8_SHOP_PENDING_PURCHASE_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore */
  }
}

export function takePendingShopPurchase() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(C8_SHOP_PENDING_PURCHASE_KEY);
    sessionStorage.removeItem(C8_SHOP_PENDING_PURCHASE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function clearPendingShopPurchase() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(C8_SHOP_PENDING_PURCHASE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function trackShopView({ user_id, sport, pathname }) {
  const loggedIn = user_id != null && user_id !== "";
  pushEvent(
    "shop_view",
    { sport: sport !== undefined ? sport : getLastSport() },
    {
      pathname: pathname || (typeof window !== "undefined" ? window.location.pathname : "/shop"),
      userStatus: loggedIn ? "logged_in" : "guest",
      userId: user_id,
    }
  );
}

export function trackProductSelected({
  user_id,
  product_id,
  product_type,
  product_name,
  price,
  original_price,
  discount_pct,
  currency,
  sport,
  pathname,
}) {
  pushEvent(
    "product_selected",
    stripNilFields({
      product_id: product_id != null ? String(product_id) : undefined,
      product_type,
      product_name,
      price,
      original_price,
      discount_pct,
      currency: normalizeShopCurrency(currency),
      sport: sport !== undefined ? sport : getLastSport(),
    }),
    {
      pathname,
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Shop purchase completed. Prefer server Measurement Protocol; client helper kept for tests/docs. */
export function trackPurchaseSuccess({
  user_id,
  transaction_id,
  amount,
  currency,
  payment_method,
  product_id,
  product_type,
  sport,
  pathname,
}) {
  pushEvent(
    "purchase_success",
    stripNilFields({
      transaction_id,
      amount,
      currency: currency != null ? normalizeShopCurrency(currency) : undefined,
      payment_method,
      product_id: product_id != null ? String(product_id) : undefined,
      product_type,
      sport: sport !== undefined ? sport : getLastSport(),
    }),
    {
      pathname,
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

export function trackPurchaseFailed({
  user_id,
  product_id,
  product_type,
  amount,
  currency,
  payment_method,
  error_type,
  sport,
  pathname,
}) {
  pushEvent(
    "purchase_failed",
    stripNilFields({
      product_id: product_id != null ? String(product_id) : undefined,
      product_type,
      amount,
      currency: currency != null ? normalizeShopCurrency(currency) : undefined,
      payment_method,
      error_type,
      sport: sport !== undefined ? sport : getLastSport(),
    }),
    {
      pathname,
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Authenticated account route view — includes coin/ruby balance and XP state for segmentation. */
export function trackAccountView({
  user_id,
  coin_balance,
  ruby_balance,
  xp_crown,
  xp_rank,
  xp_value,
  login_streak_current,
  pathname,
}) {
  const num = (v) =>
    v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  pushEvent(
    "account_view",
    stripNilFields({
      coin_balance: num(coin_balance),
      ruby_balance: num(ruby_balance),
      xp_crown:
        xp_crown != null && String(xp_crown).trim() !== ""
          ? String(xp_crown).trim().toLowerCase()
          : undefined,
      xp_rank: num(xp_rank),
      xp_value: num(xp_value),
      login_streak_current: num(login_streak_current),
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/account"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** User responded to account deletion confirmation (FE intent only, not server outcome). */
export function trackAccountDeleteResponse({
  user_id,
  delete_response,
  pathname,
}) {
  const response =
    delete_response === "confirmed" || delete_response === "cancelled"
      ? delete_response
      : undefined;
  pushEvent(
    "account_delete_response",
    stripNilFields({
      delete_response: response,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/account"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Marketing email consent persisted (compliance). */
export function trackMarketingConsentUpdate({
  user_id,
  previous_status,
  new_status,
  pathname,
}) {
  pushEvent(
    "marketing_consent_update",
    {
      previous_status: Boolean(previous_status),
      new_status: Boolean(new_status),
    },
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/account"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Catalog avatar saved on account (e.g. key `giraffe-orange`). */
export function trackAvatarSelected({ user_id, avatar_id, pathname }) {
  pushEvent(
    "avatar_selected",
    { avatar_id },
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/account"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

// --- Trader (GTM) — discovery & gameplay funnel ---

export function normalizeTraderSport(raw) {
  if (raw == null || String(raw).trim() === "") return undefined;
  const s = String(raw).toLowerCase().trim();
  // Never invent a fake controlled value — omit so sticky clear can null the key.
  if (s === "unknown") return undefined;
  if (s.includes("horse")) return "horses";
  if (s.includes("cricket")) return "cricket";
  if (s.includes("foot") || s.includes("soccer")) return "football";
  if (s.includes("tennis")) return "tennis";
  return s;
}

/** Best-effort prediction / trade / play id from a place-trade or squares submit response. */
export function resolvePredictionIdFromSubmitResponse(payload) {
  if (payload == null || typeof payload !== "object") return undefined;
  const data =
    payload.data != null && typeof payload.data === "object" ? payload.data : null;
  const candidates = [
    data?.tradeId,
    data?.playId,
    data?.prediction_id,
    data?.id,
    payload.tradeId,
    payload.playId,
    payload.prediction_id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") return String(c);
  }
  return undefined;
}

function traderPushContext(user_id, pathname) {
  const loggedIn = user_id != null && user_id !== "";
  return {
    pathname:
      pathname ||
      (typeof window !== "undefined" ? window.location.pathname : "/trader"),
    userStatus: loggedIn ? "logged_in" : "guest",
    userId: user_id,
  };
}

/** Retention: My Games drawer or full page opened. */
export function trackMyGamesView({ user_id, active_games_count, pathname }) {
  const n =
    active_games_count != null && !Number.isNaN(Number(active_games_count))
      ? Number(active_games_count)
      : undefined;
  pushEvent(
    "my_games_view",
    stripNilFields({ active_games_count: n }),
    traderPushContext(user_id, pathname)
  );
}

/** Rules / how-to-play drawer opened. */
export function trackHowToPlayView({ user_id, game_type, entry_point, pathname }) {
  pushEvent(
    "how_to_play_view",
    stripNilFields({
      game_type:
        game_type != null && String(game_type) !== ""
          ? String(game_type)
          : undefined,
      entry_point:
        entry_point != null && String(entry_point) !== ""
          ? String(entry_point)
          : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackSpinStarted({ user_id, pathname }) {
  pushEvent(
    "spin_started",
    { game_type: "wheel-spin" },
    traderPushContext(user_id, pathname)
  );
}

export function trackWheelTriviaStarted({ user_id, pathname }) {
  pushEvent(
    "wheel_trivia_started",
    { game_type: "wheel-spin" },
    traderPushContext(user_id, pathname)
  );
}

export function trackQuickfireStarted({ user_id, pathname }) {
  pushEvent(
    "quickfire_started",
    { game_type: "quickfire" },
    traderPushContext(user_id, pathname)
  );
}

/** Shown when a game has no playable session (supply gap / schedule). */
export function trackGameUnavailableView({
  user_id,
  game_type,
  unavailable_reason,
  fixture_id,
  competition_name,
  sport,
  match_status,
  opens_in_minutes,
  pathname,
}) {
  const gt =
    game_type != null && String(game_type).trim() !== ""
      ? String(game_type).trim().toLowerCase()
      : undefined;
  const ur =
    unavailable_reason != null && String(unavailable_reason).trim() !== ""
      ? String(unavailable_reason).trim().toLowerCase()
      : undefined;
  const opens =
    opens_in_minutes != null && !Number.isNaN(Number(opens_in_minutes))
      ? Number(opens_in_minutes)
      : undefined;
  pushEvent(
    "game_unavailable_view",
    stripNilFields({
      game_type: gt,
      unavailable_reason: ur,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      sport: sport != null ? normalizeTraderSport(sport) : undefined,
      match_status:
        match_status != null && String(match_status).trim() !== ""
          ? String(match_status).trim().toLowerCase()
          : undefined,
      opens_in_minutes: opens,
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Guest spin funnel: signup nudge shown after spin (auto-redirect or collect CTA).
 * Fires once per tab session per game session + prompt_type (see guestSpinRegistrationPromptTracking.ts).
 */
export function trackRegistrationPromptShown({
  user_id,
  prompt_type,
  game_session_id,
  reward_amount,
  currency,
  source_page,
  pathname,
}) {
  const pt =
    prompt_type != null && String(prompt_type).trim() !== ""
      ? String(prompt_type).trim().toLowerCase()
      : undefined;
  const reward =
    reward_amount != null && !Number.isNaN(Number(reward_amount))
      ? Number(reward_amount)
      : undefined;
  const c =
    currency != null && String(currency).trim() !== ""
      ? normalizeShopCurrency(currency)
      : undefined;
  pushEvent(
    "registration_prompt_shown",
    stripNilFields({
      prompt_type: pt === "collect_cta" ? "collect_cta" : pt === "auto_redirect" ? "auto_redirect" : undefined,
      game_session_id: game_session_id != null ? String(game_session_id) : undefined,
      game_type: "wheel-spin",
      reward_amount: reward,
      currency: c,
      source_page:
        source_page != null && String(source_page).trim() !== ""
          ? String(source_page).trim().toLowerCase()
          : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Daily wheel: prize credited via collectwinnings (logged-in only). */
export function trackSpinComplete({
  user_id,
  reward_amount,
  currency,
  reward_type,
  spin_result,
  daily_bonus_streak_current,
  pathname,
}) {
  const n =
    reward_amount != null && !Number.isNaN(Number(reward_amount))
      ? Number(reward_amount)
      : undefined;
  const c =
    currency != null && String(currency).trim() !== ""
      ? normalizeShopCurrency(currency)
      : "COINS";
  // Derive reward_type from currency when not explicitly provided.
  let rt = reward_type;
  if (rt == null) {
    if (c === "RUBIES") rt = "rubies";
    else if (c === "COINS") rt = "coins";
  }
  const streak =
    daily_bonus_streak_current != null && !Number.isNaN(Number(daily_bonus_streak_current))
      ? Number(daily_bonus_streak_current)
      : undefined;
  pushEvent(
    "spin_complete",
    stripNilFields({
      reward_amount: n,
      currency: c,
      reward_type: rt,
      spin_result:
        spin_result != null && String(spin_result).trim() !== ""
          ? String(spin_result)
          : undefined,
      daily_bonus_streak_current: streak,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Wheel trivia multiplier: bonus credited via triviaendgame. */
export function trackWheelTriviaComplete({ user_id, reward_amount, currency, pathname }) {
  const n =
    reward_amount != null && !Number.isNaN(Number(reward_amount))
      ? Number(reward_amount)
      : undefined;
  const c =
    currency != null && String(currency).trim() !== ""
      ? normalizeShopCurrency(currency)
      : "COINS";
  pushEvent(
    "wheel_trivia_complete",
    stripNilFields({ reward_amount: n, currency: c }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Quickfire: one event per successful quickfireendgame (each GAME_DONE).
 * Users who pause and resume get multiple events; reward_amount is coins from that batch only.
 * session_complete === true when today's question set is finished (answered >= total).
 */
export function trackQuickfireComplete({
  user_id,
  reward_amount,
  currency,
  questions_answered,
  questions_total,
  questions_correct,
  questions_incorrect,
  pathname,
}) {
  const n =
    reward_amount != null && !Number.isNaN(Number(reward_amount))
      ? Number(reward_amount)
      : undefined;
  const c =
    currency != null && String(currency).trim() !== ""
      ? normalizeShopCurrency(currency)
      : "COINS";
  const answered =
    questions_answered != null && !Number.isNaN(Number(questions_answered))
      ? Number(questions_answered)
      : undefined;
  const total =
    questions_total != null && !Number.isNaN(Number(questions_total))
      ? Number(questions_total)
      : undefined;
  const correct =
    questions_correct != null && !Number.isNaN(Number(questions_correct))
      ? Number(questions_correct)
      : undefined;
  const incorrect =
    questions_incorrect != null && !Number.isNaN(Number(questions_incorrect))
      ? Number(questions_incorrect)
      : undefined;
  let session_complete;
  if (answered !== undefined && total !== undefined && total > 0) {
    session_complete = answered >= total;
  }
  pushEvent(
    "quickfire_complete",
    stripNilFields({
      reward_amount: n,
      currency: c,
      questions_answered: answered,
      questions_total: total,
      questions_correct: correct,
      questions_incorrect: incorrect,
      session_complete,
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderSportSelected({ user_id, sport, pathname }) {
  const normalized = normalizeTraderSport(sport);
  persistLastSport(normalized);
  pushEvent(
    "sport_selected",
    { sport: normalized },
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderLeagueSelected({
  user_id,
  sport,
  competition_name,
  pathname,
}) {
  pushEvent(
    "league_selected",
    stripNilFields({
      sport: normalizeTraderSport(sport),
      competition_name:
        competition_name != null ? String(competition_name) : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderMatchSelected({
  user_id,
  fixture_id,
  sport,
  competition_name,
  entry_point,
  game_type = "trader",
  pathname,
}) {
  const gt = normalizeGameType(game_type) || "trader";
  if (entry_point) {
    persistGameEntryPoint({ entry_point, fixture_id, game_type: gt });
  }
  const resolvedEntryPoint = withResolvedEntryPoint(entry_point, { fixture_id, game_type: gt });
  pushEvent(
    "match_selected",
    stripNilFields({
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type: gt,
      sport: normalizeTraderSport(sport),
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      entry_point: resolvedEntryPoint,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Squares: same `match_selected` as Trader list; sport is always cricket. */
export function trackSquaresMatchSelected({
  user_id,
  fixture_id,
  competition_name,
  entry_point,
  pathname,
}) {
  trackTraderMatchSelected({
    user_id,
    fixture_id,
    sport: "cricket",
    competition_name,
    entry_point,
    game_type: "squares",
    pathname,
  });
}

/** Bullion: same `match_selected` event as Trader/Squares; sport is always cricket. */
export function trackBullionMatchSelected({
  user_id,
  fixture_id,
  competition_name,
  entry_point,
  pathname,
}) {
  trackTraderMatchSelected({
    user_id,
    fixture_id,
    sport: "cricket",
    competition_name,
    entry_point,
    game_type: "bullion",
    pathname,
  });
}

/** Sixes: same `match_selected` event as Trader/Squares; sport is always cricket. */
export function trackSixesMatchSelected({
  user_id,
  fixture_id,
  competition_name,
  entry_point,
  pathname,
}) {
  trackTraderMatchSelected({
    user_id,
    fixture_id,
    sport: "cricket",
    competition_name,
    entry_point,
    game_type: "sixes",
    pathname,
  });
}

/** Squares match play: share / invite CTA (header or mobile bottom bar). */
export function trackPlayWithFriendClicked({
  user_id,
  cta_name,
  cta_location,
  fixture_id,
  game_type,
  visibility,
  pathname,
}) {
  const loggedIn = user_id != null && user_id !== "";
  pushEvent(
    "play_with_friend_clicked",
    stripNilFields({
      cta_name:
        cta_name != null && String(cta_name) !== ""
          ? String(cta_name)
          : undefined,
      cta_location:
        cta_location != null && String(cta_location) !== ""
          ? String(cta_location)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type:
        game_type != null && String(game_type).trim() !== ""
          ? String(game_type).trim().toLowerCase()
          : undefined,
      visibility:
        visibility === "private" || visibility === "public" ? visibility : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/squares"),
      userStatus: loggedIn ? "logged_in" : "guest",
      userId: loggedIn ? user_id : undefined,
    }
  );
}

export function trackGameStarted({
  user_id,
  fixture_id,
  game_type,
  sport,
  competition_name,
  is_multiplayer,
  players_count,
  entry_point,
  pathname,
  play_mode,
  league_id,
}) {
  const gt = normalizeGameType(game_type);
  // Squares / Bullion / Sixes are cricket-only products.
  const resolvedSport =
    gt === "squares" || gt === "bullion" || gt === "sixes"
      ? "cricket"
      : normalizeTraderSport(sport);
  const resolvedEntry = withResolvedEntryPoint(
    entry_point,
    { fixture_id, game_type: gt, pathname },
    { fallback: true }
  );
  // Remember for later funnel steps in this match (stake / prediction).
  if (resolvedEntry) {
    persistGameEntryPoint({
      entry_point: resolvedEntry,
      fixture_id,
      game_type: gt,
    });
  }
  pushEvent(
    "game_started",
    withLeaguePlayMode(
      stripNilFields({
        fixture_id: fixture_id != null ? String(fixture_id) : undefined,
        game_type: gt,
        sport: resolvedSport,
        competition_name:
          competition_name != null && String(competition_name) !== ""
            ? String(competition_name)
            : undefined,
        is_multiplayer:
          typeof is_multiplayer === "boolean" ? is_multiplayer : undefined,
        players_count:
          players_count != null && !Number.isNaN(Number(players_count))
            ? Number(players_count)
            : undefined,
        entry_point: resolvedEntry,
      }),
      { play_mode, league_id }
    ),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderGameEntryClicked({
  user_id,
  fixture_id,
  game_type,
  sport,
  entry_point,
  pathname,
}) {
  const gt = normalizeGameType(game_type) || "trader";
  if (entry_point) {
    persistGameEntryPoint({ entry_point, fixture_id, game_type: gt });
  }
  const resolvedSport =
    gt === "squares" || gt === "bullion" || gt === "sixes"
      ? "cricket"
      : normalizeTraderSport(sport);
  pushEvent(
    "game_entry_clicked",
    stripNilFields({
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type: gt,
      sport: resolvedSport,
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: gt, pathname },
        { fallback: true }
      ),
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Same data layer event as Trader; use `game_type` to segment (e.g. trader | squares). */
export function trackGameLobbyView({
  user_id,
  fixture_id,
  game_type,
  sport,
  competition_name,
  entry_point,
  source_page,
  match_status,
  available_match_count,
  not_open_match_count,
  in_play_match_count,
  pathname,
}) {
  const count = (v) =>
    v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  const gt = normalizeGameType(game_type);
  const resolvedEntry =
    entry_point != null && String(entry_point) !== ""
      ? String(entry_point)
      : withResolvedEntryPoint(undefined, { fixture_id, game_type: gt, pathname }, { fallback: true });
  // Lobby view seeds session so match play / submit can attribute without a card click.
  if (resolvedEntry && gt) {
    persistGameEntryPoint({
      entry_point: resolvedEntry,
      fixture_id: fixture_id ?? null,
      game_type: gt,
    });
  }
  // Squares / Bullion / Sixes are cricket-only — never emit unknown/missing sport.
  const resolvedSport =
    gt === "squares" || gt === "bullion" || gt === "sixes"
      ? "cricket"
      : normalizeTraderSport(sport);
  pushEvent(
    "game_lobby_view",
    stripNilFields({
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type: gt,
      sport: resolvedSport,
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      entry_point: resolvedEntry,
      source_page:
        source_page != null && String(source_page) !== ""
          ? String(source_page)
          : undefined,
      match_status:
        match_status != null && String(match_status).trim() !== ""
          ? String(match_status).trim().toLowerCase()
          : undefined,
      available_match_count: count(available_match_count),
      not_open_match_count: count(not_open_match_count),
      in_play_match_count: count(in_play_match_count),
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderGameLobbyView(params) {
  return trackGameLobbyView({
    ...params,
    game_type: params?.game_type ?? "trader",
  });
}

export function trackTraderMarketGroupSelected({
  user_id,
  sport,
  market_group,
  previous_market_group,
  fixture_id,
  competition_name,
  entry_point,
  pathname,
}) {
  pushEvent(
    "market_group_selected",
    stripNilFields({
      game_type: "trader",
      sport: normalizeTraderSport(sport),
      market_group:
        market_group != null ? String(market_group) : undefined,
      previous_market_group:
        previous_market_group != null
          ? String(previous_market_group)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: "trader", pathname },
        { fallback: true }
      ),
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderOutcomeSelected({
  user_id,
  sport,
  market_name,
  selection_value,
  odds,
  competition_name,
  fixture_id,
  is_multiplayer,
  players_count,
  market_group,
  market_status,
  odds_available,
  entry_point,
  pathname,
}) {
  const o =
    odds != null && !Number.isNaN(Number(odds)) ? Number(odds) : undefined;
  const pc =
    players_count != null && !Number.isNaN(Number(players_count))
      ? Number(players_count)
      : undefined;
  pushEvent(
    "outcome_selected",
    stripNilFields({
      game_type: "trader",
      sport: normalizeTraderSport(sport),
      market_name:
        market_name != null ? String(market_name) : undefined,
      selection_value:
        selection_value != null ? String(selection_value) : undefined,
      odds: o,
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      is_multiplayer:
        typeof is_multiplayer === "boolean" ? is_multiplayer : undefined,
      players_count: pc,
      market_group:
        market_group != null && String(market_group) !== ""
          ? String(market_group)
          : undefined,
      market_status:
        market_status != null && String(market_status).trim() !== ""
          ? String(market_status).trim().toLowerCase()
          : undefined,
      odds_available:
        typeof odds_available === "boolean" ? odds_available : undefined,
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: "trader", pathname },
        { fallback: true }
      ),
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderStakeSelected({
  user_id,
  sport,
  stake_amount,
  currency,
  competition_name,
  fixture_id,
  is_multiplayer,
  players_count,
  stake_source,
  entry_point,
  pathname,
}) {
  const n =
    stake_amount != null && !Number.isNaN(Number(stake_amount))
      ? Number(stake_amount)
      : undefined;
  const pc =
    players_count != null && !Number.isNaN(Number(players_count))
      ? Number(players_count)
      : undefined;
  pushEvent(
    "stake_selected",
    stripNilFields({
      game_type: "trader",
      sport: normalizeTraderSport(sport),
      stake_amount: n,
      currency: currency != null ? normalizeShopCurrency(currency) : "COINS",
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      is_multiplayer:
        typeof is_multiplayer === "boolean" ? is_multiplayer : undefined,
      players_count: pc,
      stake_source:
        stake_source != null && String(stake_source).trim() !== ""
          ? String(stake_source).trim().toLowerCase()
          : undefined,
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: "trader", pathname },
        { fallback: true }
      ),
    }),
    traderPushContext(user_id, pathname)
  );
}

export function trackTraderPredictionSubmitted({
  user_id,
  sport,
  stake_amount,
  odds,
  prediction_id,
  market_name,
  selection_value,
  competition_name,
  fixture_id,
  is_multiplayer,
  players_count,
  entry_point,
  pathname,
  play_mode,
  league_id,
}) {
  const stake =
    stake_amount != null && !Number.isNaN(Number(stake_amount))
      ? Number(stake_amount)
      : undefined;
  const o =
    odds != null && !Number.isNaN(Number(odds)) ? Number(odds) : undefined;
  const pc =
    players_count != null && !Number.isNaN(Number(players_count))
      ? Number(players_count)
      : undefined;
  pushEvent(
    "prediction_submitted",
    withLeaguePlayMode(
      stripNilFields({
        game_type: "trader",
        submission_type: "trader_prediction",
        sport:
          sport != null && String(sport).trim() !== ""
            ? normalizeTraderSport(sport)
            : undefined,
        stake_amount: stake,
        odds: o,
        prediction_id:
          prediction_id != null && String(prediction_id).trim() !== ""
            ? String(prediction_id)
            : undefined,
        market_name:
          market_name != null ? String(market_name) : undefined,
        selection_value:
          selection_value != null ? String(selection_value) : undefined,
        competition_name:
          competition_name != null && String(competition_name) !== ""
            ? String(competition_name)
            : undefined,
        fixture_id: fixture_id != null ? String(fixture_id) : undefined,
        is_multiplayer:
          typeof is_multiplayer === "boolean" ? is_multiplayer : undefined,
        players_count: pc,
        entry_point: withResolvedEntryPoint(
          entry_point,
          { fixture_id, game_type: "trader", pathname },
          { fallback: true }
        ),
      }),
      { play_mode, league_id }
    ),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Squares grid prediction confirmed by the backend. Shared `prediction_submitted`
 * event with `submission_type: "squares_prediction"`.
 */
export function trackSquaresPredictionSubmitted({
  user_id,
  fixture_id,
  competition_name,
  prediction_id,
  stake_amount,
  is_multiplayer,
  players_count,
  entry_point,
  pathname,
  play_mode,
  league_id,
}) {
  pushEvent(
    "prediction_submitted",
    withLeaguePlayMode(
      stripNilFields({
        game_type: "squares",
        submission_type: "squares_prediction",
        sport: "cricket",
        fixture_id: fixture_id != null ? String(fixture_id) : undefined,
        competition_name: asStr(competition_name),
        prediction_id:
          prediction_id != null && String(prediction_id).trim() !== ""
            ? String(prediction_id)
            : undefined,
        stake_amount: asNum(stake_amount),
        is_multiplayer: asBool(is_multiplayer),
        players_count: asNum(players_count),
        entry_point: withResolvedEntryPoint(
          entry_point,
          { fixture_id, game_type: "squares", pathname },
          { fallback: true }
        ),
      }),
      { play_mode, league_id }
    ),
    traderPushContext(user_id, pathname)
  );
}

/**
 * User exited/cancelled a prediction flow before submission.
 * `last_step`: `none` (no selection), `outcome` (selected, no stake), `stake` (stake set, not submitted).
 */
export function trackPredictionCancelled({
  user_id,
  game_type,
  sport,
  fixture_id,
  competition_name,
  last_step,
  market_name,
  selection_value,
  stake_amount,
  pathname,
}) {
  const stake =
    stake_amount != null && !Number.isNaN(Number(stake_amount))
      ? Number(stake_amount)
      : undefined;
  const step =
    last_step === "outcome" || last_step === "stake" || last_step === "none"
      ? last_step
      : undefined;
  pushEvent(
    "prediction_cancelled",
    stripNilFields({
      game_type:
        game_type != null && String(game_type) !== ""
          ? String(game_type)
          : undefined,
      sport: sport != null ? normalizeTraderSport(sport) : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name:
        competition_name != null && String(competition_name) !== ""
          ? String(competition_name)
          : undefined,
      last_step: step,
      market_name:
        market_name != null && String(market_name) !== ""
          ? String(market_name)
          : undefined,
      selection_value:
        selection_value != null && String(selection_value) !== ""
          ? String(selection_value)
          : undefined,
      stake_amount: stake,
    }),
    traderPushContext(user_id, pathname)
  );
}

// --- New game modes: Bullion & Sixes (GTM / GA4 custom events) ---

function asNum(v) {
  return v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
}
function asStr(v) {
  return v != null && String(v) !== "" ? String(v) : undefined;
}
function asLowerStr(v) {
  return v != null && String(v).trim() !== ""
    ? String(v).trim().toLowerCase()
    : undefined;
}
function asBool(v) {
  return typeof v === "boolean" ? v : undefined;
}

/**
 * Intermediary gameplay interaction not covered by a more specific v1 event.
 * `interaction_type`: chip_placed | lucky_dip_clicked | gold_bullion_bar_placed |
 * grid_cell_selected | private_battle_link_shared | public_game_link_shared
 */
export function trackGameInteraction({
  user_id,
  interaction_type,
  interaction_value,
  game_type,
  fixture_id,
  game_id,
  sport,
  competition_name,
  is_multiplayer,
  players_count,
  pathname,
}) {
  pushEvent(
    "game_interaction",
    stripNilFields({
      interaction_type: asStr(interaction_type),
      interaction_value: asStr(interaction_value),
      game_type: asStr(game_type),
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_id: game_id != null ? String(game_id) : undefined,
      sport: asLowerStr(sport),
      competition_name: asStr(competition_name),
      is_multiplayer: asBool(is_multiplayer),
      players_count: asNum(players_count),
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Bullion entry confirmed by the backend. Shared `prediction_submitted` event with
 * `submission_type: "bullion_entry"`. `visibility`: public | private.
 */
export function trackBullionEntrySubmitted({
  user_id,
  sport,
  fixture_id,
  game_id,
  competition_name,
  entry_stake,
  is_multiplayer,
  players_count,
  visibility,
  is_update,
  entry_point,
  pathname,
  play_mode,
  league_id,
}) {
  const vis =
    visibility === "public" || visibility === "private" ? visibility : undefined;
  pushEvent(
    "prediction_submitted",
    withLeaguePlayMode(
      stripNilFields({
        game_type: "bullion",
        submission_type: "bullion_entry",
        sport: "cricket",
        fixture_id: fixture_id != null ? String(fixture_id) : undefined,
        game_id: game_id != null ? String(game_id) : undefined,
      competition_name: asStr(competition_name),
      entry_stake: asNum(entry_stake),
      is_multiplayer: asBool(is_multiplayer),
      players_count: asNum(players_count),
      visibility: vis,
      is_update: asBool(is_update),
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: "bullion", pathname },
        { fallback: true }
      ),
      }),
      { play_mode, league_id }
    ),
    traderPushContext(user_id, pathname)
  );
}

/** Sixes card shown to the user for the first time (after backend confirmation). */
export function trackSixesCardRevealed({
  user_id,
  fixture_id,
  competition_name,
  sport,
  sixes_card_id,
  sixes_match_id,
  sixes_competition,
  sixes_sport,
  sixes_total_cells,
  sixes_card_date,
  pathname,
}) {
  pushEvent(
    "sixes_card_revealed",
    stripNilFields({
      game_type: "sixes",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name: asStr(competition_name),
      sport: asLowerStr(sport),
      sixes_card_id: sixes_card_id != null ? String(sixes_card_id) : undefined,
      sixes_match_id:
        sixes_match_id != null ? String(sixes_match_id) : undefined,
      sixes_competition: asStr(sixes_competition),
      sixes_sport: asLowerStr(sixes_sport),
      sixes_total_cells: asNum(sixes_total_cells),
      sixes_card_date: asStr(sixes_card_date),
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Sixes cheer/multiplier player became active.
 * `sixes_multiplier_selection_type`: assigned (default on reveal) | switched (user changed).
 */
export function trackSixesMultiplierSelected({
  user_id,
  fixture_id,
  competition_name,
  sport,
  sixes_card_id,
  sixes_multiplier_selection_type,
  sixes_multiplier_player_name,
  sixes_multiplier_team_name,
  sixes_multiplier_market_id,
  ruby_cost,
  ruby_balance_before,
  ruby_balance_after,
  pathname,
}) {
  const selType =
    sixes_multiplier_selection_type === "assigned" ||
    sixes_multiplier_selection_type === "switched"
      ? sixes_multiplier_selection_type
      : undefined;
  pushEvent(
    "sixes_multiplier_selected",
    stripNilFields({
      game_type: "sixes",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name: asStr(competition_name),
      sport: asLowerStr(sport),
      sixes_card_id: sixes_card_id != null ? String(sixes_card_id) : undefined,
      sixes_multiplier_selection_type: selType,
      sixes_multiplier_player_name: asStr(sixes_multiplier_player_name),
      sixes_multiplier_team_name: asStr(sixes_multiplier_team_name),
      sixes_multiplier_market_id: asStr(sixes_multiplier_market_id),
      ruby_cost: asNum(ruby_cost),
      ruby_balance_before: asNum(ruby_balance_before),
      ruby_balance_after: asNum(ruby_balance_after),
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Sixes card submitted/locked after backend confirmation. Shared `prediction_submitted`
 * event with `submission_type: "sixes_card"`.
 */
export function trackSixesCardSubmitted({
  user_id,
  fixture_id,
  competition_name,
  sport,
  is_multiplayer,
  players_count,
  sixes_card_id,
  sixes_match_id,
  sixes_competition,
  sixes_sport,
  sixes_multiplier_selected,
  sixes_multiplier_selection_type,
  sixes_multiplier_player_name,
  sixes_total_cells,
  sixes_card_date,
  entry_point,
  pathname,
  play_mode,
  league_id,
}) {
  const selType =
    sixes_multiplier_selection_type === "assigned" ||
    sixes_multiplier_selection_type === "switched"
      ? sixes_multiplier_selection_type
      : undefined;
  pushEvent(
    "prediction_submitted",
    withLeaguePlayMode(
      stripNilFields({
        game_type: "sixes",
        submission_type: "sixes_card",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name: asStr(competition_name),
      sport: "cricket",
      is_multiplayer: asBool(is_multiplayer),
      players_count: asNum(players_count),
      sixes_card_id: sixes_card_id != null ? String(sixes_card_id) : undefined,
      sixes_match_id:
        sixes_match_id != null ? String(sixes_match_id) : undefined,
      sixes_competition: asStr(sixes_competition),
      sixes_sport: asLowerStr(sixes_sport) ?? "cricket",
      sixes_multiplier_selected: asBool(sixes_multiplier_selected),
      sixes_multiplier_selection_type: selType,
      sixes_multiplier_player_name: asStr(sixes_multiplier_player_name),
      sixes_total_cells: asNum(sixes_total_cells),
      sixes_card_date: asStr(sixes_card_date),
      entry_point: withResolvedEntryPoint(
        entry_point,
        { fixture_id, game_type: "sixes", pathname },
        { fallback: true }
      ),
      }),
      { play_mode, league_id }
    ),
    traderPushContext(user_id, pathname)
  );
}

/** Sixes card settled (card-level outcome). Prefer server Measurement Protocol; client helper kept for tests/docs. `sixes_outcome`: settled | cancelled. */
export function trackSixesCardSettled({
  user_id,
  fixture_id,
  competition_name,
  sport,
  sixes_card_id,
  sixes_total_coins_earned,
  sixes_wins_count,
  sixes_sets_count,
  sixes_settled_cells,
  sixes_total_cells,
  sixes_multiplier_sixes,
  sixes_outcome,
  pathname,
}) {
  const outcome =
    sixes_outcome === "settled" || sixes_outcome === "cancelled"
      ? sixes_outcome
      : undefined;
  pushEvent(
    "sixes_card_settled",
    stripNilFields({
      game_type: "sixes",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name: asStr(competition_name),
      sport: asLowerStr(sport),
      sixes_card_id: sixes_card_id != null ? String(sixes_card_id) : undefined,
      sixes_total_coins_earned: asNum(sixes_total_coins_earned),
      sixes_wins_count: asNum(sixes_wins_count),
      sixes_sets_count: asNum(sixes_sets_count),
      sixes_settled_cells: asNum(sixes_settled_cells),
      sixes_total_cells: asNum(sixes_total_cells),
      sixes_multiplier_sixes: asNum(sixes_multiplier_sixes),
      sixes_outcome: outcome,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Sixes match board or completed board viewed. `sixes_card_status`: active | settled | cancelled. */
export function trackSixesBoardViewed({
  user_id,
  fixture_id,
  competition_name,
  sport,
  sixes_match_id,
  sixes_board_complete,
  sixes_card_id,
  sixes_card_status,
  sixes_settled_cells,
  pathname,
}) {
  const status =
    sixes_card_status === "active" ||
    sixes_card_status === "settled" ||
    sixes_card_status === "cancelled"
      ? sixes_card_status
      : undefined;
  pushEvent(
    "sixes_board_viewed",
    stripNilFields({
      game_type: "sixes",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      competition_name: asStr(competition_name),
      sport: asLowerStr(sport),
      sixes_match_id:
        sixes_match_id != null ? String(sixes_match_id) : undefined,
      sixes_board_complete: asBool(sixes_board_complete),
      sixes_card_id: sixes_card_id != null ? String(sixes_card_id) : undefined,
      sixes_card_status: status,
      sixes_settled_cells: asNum(sixes_settled_cells),
    }),
    traderPushContext(user_id, pathname)
  );
}

// --- Friends & invites (GTM / GA4 custom events) ---

/** Invite UI opened or native share sheet invoked — funnel before `friend_invite_sent`. */
export function trackInviteFlowOpened({
  user_id,
  entry_point,
  fixture_id,
  game_type,
  visibility,
  pathname,
}) {
  const loggedIn = user_id != null && user_id !== "";
  pushEvent(
    "invite_flow_opened",
    stripNilFields({
      entry_point:
        entry_point != null && String(entry_point) !== ""
          ? String(entry_point)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type:
        game_type != null && String(game_type).trim() !== ""
          ? String(game_type).trim().toLowerCase()
          : undefined,
      visibility:
        visibility === "private" || visibility === "public" ? visibility : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/"),
      userStatus: loggedIn ? "logged_in" : "guest",
      userId: loggedIn ? user_id : undefined,
    }
  );
}

/** Own profile link copied or shared from Profile / My Zone. */
export function trackProfileLinkShared({ user_id, invite_method, sender_user_id, pathname }) {
  pushEvent(
    "profile_link_shared",
    stripNilFields({
      invite_method:
        invite_method === "share_sheet"
          ? "share_sheet"
          : invite_method === "link"
            ? "link"
            : invite_method != null && String(invite_method).trim() !== ""
              ? String(invite_method).trim().toLowerCase()
              : "link",
      sender_user_id:
        sender_user_id != null && String(sender_user_id).trim() !== ""
          ? String(sender_user_id).trim()
          : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Recipient opened someone else's shareable `/profile` URL. */
export function trackProfileLinkJoined({
  user_id,
  sender_user_id,
  invite_method,
  pathname,
}) {
  pushEvent(
    "profile_link_joined",
    stripNilFields({
      sender_user_id:
        sender_user_id != null && String(sender_user_id).trim() !== ""
          ? String(sender_user_id).trim()
          : undefined,
      invite_method:
        invite_method != null && String(invite_method).trim() !== ""
          ? String(invite_method).trim().toLowerCase()
          : "link",
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Game invite link copied or native share succeeded (not profile links — use profile_link_shared). */
export function trackFriendInviteSent({
  user_id,
  invite_method,
  fixture_id,
  game_type,
  visibility,
  pathname,
}) {
  const loggedIn = user_id != null && user_id !== "";
  pushEvent(
    "friend_invite_sent",
    stripNilFields({
      invite_method:
        invite_method != null && String(invite_method) !== ""
          ? String(invite_method)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type:
        game_type != null && String(game_type).trim() !== ""
          ? String(game_type).trim().toLowerCase()
          : undefined,
      visibility:
        visibility === "private" || visibility === "public" ? visibility : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/"),
      userStatus: loggedIn ? "logged_in" : "guest",
      userId: loggedIn ? user_id : undefined,
    }
  );
}

/** Outgoing friend request successfully created (e.g. ADD TO FRIENDS on public profile). */
export function trackFriendRequestSent({
  user_id,
  sender_user_id,
  receiver_user_id,
  pathname,
}) {
  pushEvent(
    "friend_request_sent",
    stripNilFields({
      sender_user_id:
        sender_user_id != null ? String(sender_user_id) : undefined,
      receiver_user_id:
        receiver_user_id != null ? String(receiver_user_id) : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/profile"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Sender cancelled / withdrew a pending outgoing friend request. */
export function trackFriendRequestCancelled({
  user_id,
  sender_user_id,
  receiver_user_id,
  pathname,
}) {
  pushEvent(
    "friend_request_cancelled",
    stripNilFields({
      sender_user_id:
        sender_user_id != null ? String(sender_user_id) : undefined,
      receiver_user_id:
        receiver_user_id != null ? String(receiver_user_id) : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/profile"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/**
 * Recipient opened a match-specific game invite link (before auth or join).
 * Fires once per tab session per unique invite URL (see inviteLinkTracking.ts).
 */
export function trackInviteLinkClicked({
  user_id,
  invite_method,
  fixture_id,
  game_type,
  visibility,
  pathname,
}) {
  pushEvent(
    "invite_link_clicked",
    stripNilFields({
      invite_method:
        invite_method != null && String(invite_method).trim() !== ""
          ? String(invite_method).trim().toLowerCase()
          : "link",
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type:
        game_type != null && String(game_type).trim() !== ""
          ? String(game_type).trim().toLowerCase()
          : undefined,
      visibility:
        visibility === "private"
          ? "private"
          : visibility === "public"
            ? "public"
            : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Logged-in user completed invite-from-link claim (Squares, Bullion, etc.). */
export function trackFriendJoinedFromInvite({
  user_id,
  invite_method,
  fixture_id,
  game_type,
  visibility,
  entry_point,
  pathname,
}) {
  pushEvent(
    "friend_joined_from_invite",
    stripNilFields({
      invite_method:
        invite_method != null && String(invite_method) !== ""
          ? String(invite_method)
          : undefined,
      fixture_id: fixture_id != null ? String(fixture_id) : undefined,
      game_type:
        game_type != null && String(game_type) !== ""
          ? String(game_type)
          : undefined,
      visibility:
        visibility === "private" || visibility === "public" ? visibility : undefined,
      entry_point:
        entry_point != null && String(entry_point) !== ""
          ? String(entry_point)
          : undefined,
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Incoming friend request accepted or declined.
 * `friend_request_sender_user_id` = requester; `friend_request_receiver_user_id` = responder (current user).
 */
export function trackFriendRequestResponse({
  user_id,
  response,
  friend_request_sender_user_id,
  friend_request_receiver_user_id,
  pathname,
}) {
  const r =
    response === "accepted" || response === "rejected"
      ? response
      : response != null && String(response) !== ""
        ? String(response)
        : undefined;
  pushEvent(
    "friend_request_response",
    stripNilFields({
      response: r,
      friend_request_sender_user_id:
        friend_request_sender_user_id != null
          ? String(friend_request_sender_user_id)
          : undefined,
      friend_request_receiver_user_id:
        friend_request_receiver_user_id != null
          ? String(friend_request_receiver_user_id)
          : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/my-zone"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/**
 * In-app “challenge a friend” picker/modal opened (e.g. after CHALLENGE).
 * Use `game_type` to segment (e.g. squares, trader); `entry_point` for UI source.
 */
export function trackGameChallengeFlowOpened({ user_id, game_type, entry_point, pathname }) {
  const gt =
    game_type != null && String(game_type).trim() !== ""
      ? String(game_type).trim().toLowerCase()
      : undefined;
  pushEvent(
    "game_challenge_flow_opened",
    stripNilFields({
      game_type: gt,
      entry_point:
        entry_point != null && String(entry_point) !== ""
          ? String(entry_point)
          : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/my-zone"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Friends / profile: user clicked CHALLENGE next to a friend. */
export function trackChallengeFriendClicked({ user_id, cta_location, pathname }) {
  const path =
    pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "/my-zone");
  trackCtaClicked({
    user_id,
    cta_name: "challenge_friend",
    cta_location:
      cta_location != null && String(cta_location).trim() !== ""
        ? String(cta_location).trim()
        : "my_zone_friends",
    pathname: path,
    source_page: path,
  });
}

/** Controlled `cta_name` for opening the Leagues invite-friends modal. */
export const CTA_NAME_INVITE_FRIENDS = "invite_friends";

/** Controlled `cta_location` values for {@link trackLeagueInviteFriendsClicked}. */
export const LEAGUE_INVITE_CTA_LOCATION = Object.freeze({
  HEADER: "league_header",
  DOCK: "league_dock",
  TUTORIAL: "league_tutorial",
});

/**
 * League detail Invite / Invite friends CTA — opens InviteFriendsModal.
 * Distinguish surfaces via `cta_location` (`league_header` | `league_dock` | `league_tutorial`).
 */
export function trackLeagueInviteFriendsClicked({
  user_id,
  cta_location,
  fixture_id,
  pathname,
}) {
  trackCtaClicked({
    user_id,
    cta_name: CTA_NAME_INVITE_FRIENDS,
    cta_location,
    fixture_id,
    source_page: pathname,
    pathname,
  });
}

/** Controlled `cta_name` values for {@link trackLeagueResultsShareClicked}. */
export const LEAGUE_RESULTS_SHARE_CTA_NAME = Object.freeze({
  /** Podium SHARE buttons — open the share modal. */
  OPEN: "share_league_results",
  /** Modal primary button (native share sheet, or copy where unsupported). */
  SHARE: "share_league_results_send",
  /** Modal secondary COPY LINK button. */
  COPY_LINK: "copy_league_results_link",
});

/** Controlled `cta_location` values for {@link trackLeagueResultsShareClicked}. */
export const LEAGUE_RESULTS_SHARE_CTA_LOCATION = Object.freeze({
  /** SHARE pill next to "League finished!". */
  HEADER: "league_finished_header",
  /** Icon button on the "You finished …" row under the podium. */
  FINISH_ROW: "league_finished_you_row",
  MODAL: "league_results_share_modal",
});

/**
 * Finished-league results sharing. `target_url` is the minted `/leagues?r=CODE` link when known,
 * so shares can be joined to the visits they bring in.
 * @param {{ user_id?: string | number | null, cta_name: string, cta_location: string, target_url?: string, pathname?: string }} args
 */
export function trackLeagueResultsShareClicked({ user_id, cta_name, cta_location, target_url, pathname }) {
  const path =
    pathname || (typeof window !== "undefined" ? window.location.pathname : "/leagues");
  trackCtaClicked({
    user_id,
    cta_name,
    cta_location,
    target_url,
    pathname: path,
    source_page: path,
  });
}

/**
 * SELECT GAME popover: user chose Squares, Bullion, or Leagues before the challenge flow opens.
 * (`game_challenge_flow_opened` still fires when Squares/Bullion modals mount.)
 */
export function trackChallengeGameSelected({ user_id, game_type, cta_location, pathname }) {
  const gt =
    game_type != null && String(game_type).trim() !== ""
      ? String(game_type).trim().toLowerCase()
      : "";
  const path =
    pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "/my-zone");
  const cta_name =
    gt === "bullion"
      ? "challenge_select_bullion"
      : gt === "leagues"
        ? "challenge_select_leagues"
        : "challenge_select_squares";
  trackCtaClicked({
    user_id,
    cta_name,
    cta_location:
      cta_location != null && String(cta_location).trim() !== ""
        ? String(cta_location).trim()
        : "my_zone_friends",
    pathname: path,
    source_page: path,
  });
}

/**
 * In-app game challenge successfully sent to a friend.
 * `challenge_target_id` — game-specific context (e.g. Squares `match_id`, future: trader `fixture_id`).
 */
export function trackGameChallengeSent({
  user_id,
  game_type,
  entry_point,
  challenged_friend_user_id,
  challenge_target_id,
  pathname,
}) {
  const gt =
    game_type != null && String(game_type).trim() !== ""
      ? String(game_type).trim().toLowerCase()
      : undefined;
  pushEvent(
    "game_challenge_sent",
    stripNilFields({
      game_type: gt,
      entry_point:
        entry_point != null && String(entry_point) !== ""
          ? String(entry_point)
          : undefined,
      challenged_friend_user_id:
        challenged_friend_user_id != null
          ? String(challenged_friend_user_id)
          : undefined,
      challenge_target_id:
        challenge_target_id != null && String(challenge_target_id).trim() !== ""
          ? String(challenge_target_id).trim()
          : undefined,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/my-zone"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

// --- Generic CTA, profile/settings, and leaderboard events (GA4 custom events) ---

/**
 * Controlled `cta_name` for opening the spin-the-wheel drawer or modal.
 * Kept as the game name (not "daily_bonus") so future promo/reward CTAs stay distinct.
 */
export const CTA_NAME_SPIN_THE_WHEEL = "spin_the_wheel";

/** Controlled `cta_location` values for {@link trackSpinTheWheelCtaClicked}. */
export const SPIN_THE_WHEEL_CTA_LOCATION = {
  BURGER_MENU: "burger_menu",
  FOOTER: "footer",
  CORNER: "daily_free_coins_corner",
  GAMES_HOME_HEADLINE: "games_home_headline",
  MY_GAMES: "my_games",
};

/**
 * Spin-the-wheel entry: burger, footer, floating corner, games-home headline, My Games next step.
 * Always uses `cta_name=spin_the_wheel`; distinguish surfaces via `cta_location`.
 */
export function trackSpinTheWheelCtaClicked({ user_id, cta_location, pathname, source_page }) {
  trackCtaClicked({
    user_id,
    cta_name: CTA_NAME_SPIN_THE_WHEEL,
    cta_location,
    pathname,
    source_page:
      source_page ??
      pathname ??
      (typeof window !== "undefined" ? window.location.pathname : undefined),
  });
}

/**
 * Major CTA click across marketing, gameplay discovery, modal, profile, shop, or invite flows.
 * Use controlled `cta_name` / `cta_location` values from the tracking spec.
 */
export function trackCtaClicked({
  user_id,
  cta_name,
  cta_location,
  target_url,
  source_page,
  pathname,
  userStatus,
  fixture_id,
}) {
  const loggedIn = user_id != null && user_id !== "";
  pushEvent(
    "cta_clicked",
    {
      ...getSessionUtmPayloadWithNulls(),
      ...stripNilFields({
        cta_name:
          cta_name != null && String(cta_name) !== "" ? String(cta_name) : undefined,
        cta_location:
          cta_location != null && String(cta_location) !== ""
            ? String(cta_location)
            : undefined,
        target_url:
          target_url != null && String(target_url) !== ""
            ? String(target_url)
            : undefined,
        source_page:
          source_page != null && String(source_page) !== ""
            ? String(source_page)
            : undefined,
        fixture_id:
          fixture_id != null && String(fixture_id).trim() !== ""
            ? String(fixture_id).trim()
            : undefined,
      }),
    },
    {
      pathname,
      userStatus: userStatus || (loggedIn ? "logged_in" : "guest"),
      userId: loggedIn ? user_id : undefined,
    }
  );
}

/** Lime MATCH / RACE on match cards — opens match detail (not a game lobby). */
export function trackOpenMatchDetailClicked({
  user_id,
  cta_location,
  fixture_id,
  target_url,
  pathname,
}) {
  const path =
    pathname ||
    (typeof window !== "undefined" ? window.location.pathname : undefined);
  trackCtaClicked({
    user_id,
    cta_name: "open_match_detail",
    cta_location,
    fixture_id,
    target_url,
    pathname: path,
    source_page: path,
  });
}

/**
 * WATCH LIVE button clicks.
 * `cta_location` examples: homepage_match_centre | matches_page | match_detail |
 * trader_match_play | squares_match_play | bullion_match_play | sixes_match_play |
 * plus lobby locations (trader_lobby, squares_lobby, …)
 */
export function trackWatchLiveClicked({ user_id, cta_location, fixture_id, pathname }) {
  const path =
    pathname ||
    (typeof window !== "undefined" ? window.location.pathname : undefined);
  trackCtaClicked({
    user_id,
    cta_name: "watch_live",
    cta_location,
    fixture_id,
    pathname: path,
    source_page: path,
  });
}

/** Match-card / lobby game chip — same `cta_name` pattern as games-home tiles (`open_*`). */
export function trackMatchCardGameCtaClicked({
  user_id,
  game_type,
  target_url,
  cta_location,
  pathname,
  source_page,
}) {
  const ctaByGame = {
    trader: "open_trader",
    squares: "open_squares",
    bullion: "open_bullion",
    sixes: "open_sixes",
  };
  const cta_name = ctaByGame[game_type];
  if (!cta_name) return;
  trackCtaClicked({
    user_id,
    cta_name,
    cta_location,
    target_url,
    source_page: source_page ?? pathname,
    pathname,
  });
}

/**
 * Full match-card game entry funnel: `match_selected` + `game_entry_clicked` + `cta_clicked`,
 * with `entry_point` persisted for `game_started` / `prediction_submitted`.
 */
export function trackMatchCardGameEntry({
  user_id,
  game_type,
  fixture_id,
  sport,
  competition_name,
  cta_location,
  target_url,
  pathname,
  source_page,
}) {
  const entry_point = entryPointFromCtaLocation(cta_location);
  const path =
    pathname || (typeof window !== "undefined" ? window.location.pathname : undefined);
  const source =
    source_page ?? path;

  if (game_type === "trader") {
    trackTraderMatchSelected({
      user_id,
      fixture_id,
      sport,
      competition_name,
      entry_point,
      pathname: path,
    });
  } else if (game_type === "squares") {
    trackSquaresMatchSelected({
      user_id,
      fixture_id,
      competition_name,
      entry_point,
      pathname: path,
    });
  } else if (game_type === "bullion") {
    trackBullionMatchSelected({
      user_id,
      fixture_id,
      competition_name,
      entry_point,
      pathname: path,
    });
  } else if (game_type === "sixes") {
    trackSixesMatchSelected({
      user_id,
      fixture_id,
      competition_name,
      entry_point,
      pathname: path,
    });
  }

  trackTraderGameEntryClicked({
    user_id,
    fixture_id,
    game_type,
    sport: game_type === "trader" ? sport : "cricket",
    entry_point,
    pathname: path,
  });

  trackMatchCardGameCtaClicked({
    user_id,
    game_type,
    target_url,
    cta_location,
    pathname: path,
    source_page: source,
  });
}

/** Short codes so `updated_fields` stays under GA4's 100-char parameter limit. */
const PROFILE_UPDATED_FIELD_CODES = {
  country: "c",
  favorite_sports: "fs",
  favorite_cricket_players: "fcp",
  favorite_cricket_teams: "fct",
  favorite_football_teams: "fft",
  avatar_id: "a",
  username: "u",
  player_name: "pn",
};

function compactProfileUpdatedFields(updated_fields) {
  const list = Array.isArray(updated_fields)
    ? updated_fields
    : updated_fields != null && String(updated_fields) !== ""
      ? String(updated_fields).split(",")
      : [];
  const codes = list
    .map((f) => String(f || "").trim())
    .filter(Boolean)
    .map((f) => PROFILE_UPDATED_FIELD_CODES[f] || f);
  if (codes.length === 0) return undefined;
  return codes.join(",");
}

/** Profile customisation saved successfully. Send counts, not full preference lists. */
export function trackProfileUpdated({
  user_id,
  updated_fields,
  country,
  favorite_sports_count,
  favorite_cricket_players_count,
  favorite_teams_count,
  pathname,
}) {
  const count = (v) =>
    v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  const fields = compactProfileUpdatedFields(updated_fields);
  if (!fields) return;

  // Soft client dedupe: identical field set within 5s (retry / remount).
  if (typeof window !== "undefined" && user_id != null) {
    try {
      const dedupeKey = `c8_profile_updated:${user_id}:${fields}`;
      const prev = sessionStorage.getItem(dedupeKey);
      const now = Date.now();
      if (prev && now - Number(prev) < 5000) return;
      sessionStorage.setItem(dedupeKey, String(now));
    } catch (_) {
      /* ignore */
    }
  }

  pushEvent(
    "profile_updated",
    stripNilFields({
      updated_fields: fields,
      country:
        country != null && String(country).trim() !== ""
          ? String(country).trim()
          : undefined,
      favorite_sports_count: count(favorite_sports_count),
      favorite_cricket_players_count: count(favorite_cricket_players_count),
      favorite_teams_count: count(favorite_teams_count),
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/profile"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Privacy / visibility / social setting changed successfully (not marketing consent). */
export function trackUserPreferenceChanged({
  user_id,
  setting_name,
  settings_group,
  previous_value,
  new_value,
  pathname,
}) {
  const val = (v) => {
    if (typeof v === "boolean") return v;
    if (v != null && String(v) !== "") return String(v);
    return undefined;
  };
  pushEvent(
    "user_preference_changed",
    stripNilFields({
      setting_name:
        setting_name != null && String(setting_name) !== ""
          ? String(setting_name)
          : undefined,
      settings_group:
        settings_group != null && String(settings_group) !== ""
          ? String(settings_group)
          : undefined,
      previous_value: val(previous_value),
      new_value: val(new_value),
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/my-zone"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/**
 * XP leaderboard reached or filter changed (after rank/period data has loaded).
 * `leaderboard_period`: week | all_time. `leaderboard_scope`: all_players | my_friends.
 */
export function trackXpLeaderboardView({
  user_id,
  xp_rank,
  xp_value,
  xp_period_id,
  xp_period_days_remaining,
  xp_crown,
  leaderboard_entrant_count,
  leaderboard_period,
  leaderboard_scope,
  source_page,
  pathname,
}) {
  const num = (v) =>
    v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  let resolvedSource =
    source_page != null && String(source_page).trim() !== ""
      ? String(source_page).trim()
      : undefined;
  if (!resolvedSource && typeof document !== "undefined" && document.referrer) {
    try {
      const refUrl = new URL(document.referrer);
      if (typeof window !== "undefined" && refUrl.origin === window.location.origin) {
        resolvedSource = getPageName(refUrl.pathname);
      }
    } catch (_) {
      /* ignore */
    }
  }
  pushEvent(
    "xp_leaderboard_view",
    stripNilFields({
      xp_rank: num(xp_rank),
      xp_value: num(xp_value),
      xp_period_id: num(xp_period_id),
      xp_period_days_remaining: num(xp_period_days_remaining),
      xp_crown:
        xp_crown != null && String(xp_crown).trim() !== ""
          ? String(xp_crown).trim().toLowerCase()
          : undefined,
      leaderboard_entrant_count: num(leaderboard_entrant_count),
      leaderboard_period:
        leaderboard_period != null && String(leaderboard_period) !== ""
          ? String(leaderboard_period)
          : undefined,
      leaderboard_scope:
        leaderboard_scope != null && String(leaderboard_scope) !== ""
          ? String(leaderboard_scope)
          : undefined,
      source_page: resolvedSource,
    }),
    {
      pathname:
        pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "/xp-leaderboard"),
      userStatus: "logged_in",
      userId: user_id,
    }
  );
}

/** Best-effort `game_type` from an XP `reason` string (controlled values only). */
function gameTypeFromXpReason(reason) {
  const r = reason != null ? String(reason).toLowerCase() : "";
  if (!r) return undefined;
  if (r.startsWith("wheel_multiplier") || r.startsWith("spin")) return "wheel-spin";
  if (r.startsWith("quickfire")) return "quickfire";
  if (r.startsWith("trader")) return "trader";
  if (r.startsWith("squares")) return "squares";
  if (r.startsWith("bullion")) return "bullion";
  if (r.startsWith("sixes")) return "sixes";
  return undefined;
}

/**
 * Backend-confirmed XP grant. Prefer server Measurement Protocol; client helper kept for tests/docs.
 */
export function trackXpEarned({
  user_id,
  xp_amount,
  xp_reason,
  xp_total_after,
  xp_dedupe_key,
  game_type,
  pathname,
}) {
  const amount = asNum(xp_amount);
  if (amount == null || amount <= 0) return;
  const dedupe = asStr(xp_dedupe_key);
  // Persist across Header remounts so the same grant is not double-pushed.
  if (typeof window !== "undefined" && dedupe) {
    try {
      const key = `c8_xp_earned:${dedupe}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch (_) {
      /* ignore */
    }
  }
  pushEvent(
    "xp_earned",
    stripNilFields({
      xp_amount: amount,
      xp_reason: asStr(xp_reason),
      xp_total_after: asNum(xp_total_after),
      xp_dedupe_key: dedupe,
      game_type: normalizeGameType(game_type) ?? gameTypeFromXpReason(xp_reason),
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Login streak incremented. Prefer server Measurement Protocol; client helper kept for tests/docs.
 */
export function trackLoginStreakUpdated({
  user_id,
  login_streak_current,
  login_streak_is_new_record,
  xp_earned_from_streak,
  pathname,
}) {
  const current = asNum(login_streak_current);
  if (current == null) return;
  pushEvent(
    "login_streak_updated",
    stripNilFields({
      login_streak_current: current,
      login_streak_is_new_record: asBool(login_streak_is_new_record),
      xp_earned_from_streak: asNum(xp_earned_from_streak),
    }),
    traderPushContext(user_id, pathname)
  );
}

/**
 * Wheel multiplier streak updated. Prefer server Measurement Protocol; client helper kept for tests/docs.
 */
export function trackWheelMultiplierStreakUpdated({
  user_id,
  wheel_multiplier_streak_current,
  wheel_multiplier_streak_broken,
  reward_amount,
  currency,
  pathname,
}) {
  const current = asNum(wheel_multiplier_streak_current);
  if (current == null) return;
  pushEvent(
    "wheel_multiplier_streak_updated",
    stripNilFields({
      wheel_multiplier_streak_current: current,
      wheel_multiplier_streak_broken: asBool(wheel_multiplier_streak_broken),
      reward_amount: asNum(reward_amount),
      currency: asStr(currency),
    }),
    traderPushContext(user_id, pathname)
  );
}

/** Daily trader leaderboard tab opened or sport tab switched. */
export function trackTraderLeaderboardView({
  user_id,
  leaderboard_sport,
  trader_leaderboard_day_key,
  user_daily_rank,
  user_daily_profit,
  user_daily_trades_count,
  pathname,
}) {
  const num = (v) =>
    v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  pushEvent(
    "trader_leaderboard_view",
    stripNilFields({
      leaderboard_sport:
        leaderboard_sport != null ? normalizeTraderSport(leaderboard_sport) : undefined,
      trader_leaderboard_day_key:
        trader_leaderboard_day_key != null && String(trader_leaderboard_day_key) !== ""
          ? String(trader_leaderboard_day_key)
          : undefined,
      user_daily_rank: num(user_daily_rank),
      user_daily_profit: num(user_daily_profit),
      user_daily_trades_count: num(user_daily_trades_count),
    }),
    traderPushContext(user_id, pathname)
  );
}
